import React, { useContext, useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from './index.module.scss';
import api from '../../../utils/api';
import { DestinationContext } from '../../../contexts/DestinationContext';

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

const splitDomain = (destination) => {
    const split = destination.url.split(/\/\//)[1].split('.');
    const [name, tld] = split.length === 2 ? [split[0], split[1]] : [split[1], split[2]];

    return `${capitalize(name)}.${tld}`;
};

const buildNavLinks = (destinationName) => [
    { label: 'Accommodation', url: '/accommodation' },
    { label: 'Safaris', url: '/safaris' },
    { label: 'Destinations', url: '/destinations' },
    { label: `${destinationName} Guide`, url: '/useful-info' },
    { label: 'About', url: '/about-us' },
    { label: 'Terms & Conditions', url: '/terms' },
];

const FooterDestinations = () => {
    const destination = useContext(DestinationContext);
    const [countryGroups, setCountryGroups] = useState([]);

    useEffect(() => {
        let cancelled = false;

        api('get', 'destination/grouped', { orderCountry: 'asc', limit: 200, page: 1 })
            .then((response) => {
                if (!cancelled) setCountryGroups(response.data || []);
            })
            .catch(() => {
                if (!cancelled) setCountryGroups([]);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    if (!countryGroups.length) return null;

    const orderedGroups = [...countryGroups].sort((a, b) => {
        const aIsCurrent = a.name === destination?.country;
        const bIsCurrent = b.name === destination?.country;
        if (aIsCurrent && !bIsCurrent) return -1;
        if (bIsCurrent && !aIsCurrent) return 1;
        return a.name.localeCompare(b.name);
    });

    const hostname = destination?.url
        ? new URL(destination.url).hostname.replace('www.', '')
        : null;
    const [urlName, ...urlRest] = hostname ? hostname.split('.') : [];
    const urlSuffix = urlRest.length ? `.${urlRest.join('.')}` : '';

    // `destination.logo` is an explicit admin-authored text logo — e.g.
    // logoTitle "maasaimara.com" as the brand name — and takes priority
    // when set; otherwise fall back to the site's own domain.
    const logo = destination?.logo;
    const brandName = logo?.logoTitle || (urlName ? capitalize(urlName) : '');
    const brandSuffix = logo?.logoTitle ? '' : urlSuffix;
    const brandDomain = hostname ? `${capitalize(urlName)}${urlSuffix}` : '';
    const footer = destination?.footer;
    const contactPhone = footer?.contactPhone || '+254 (0)716 511 162';
    const contactEmail = footer?.contactEmail || (hostname ? `info@${hostname}` : '');
    const year = new Date().getFullYear();

    return (
        <div className={styles.container}>
            <div className={styles.grid}>
                {orderedGroups.map((countryGroup) => (
                    <div className={styles.col} key={countryGroup.name}>
                        <h6 className={styles.heading}>{countryGroup.name}</h6>
                        {countryGroup.destinations.map((dest) => (
                            <a
                                key={dest._id}
                                href={dest.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.link}
                            >
                                {splitDomain(dest)}
                            </a>
                        ))}
                    </div>
                ))}
            </div>

            {brandName && (
                <div className={styles.brandBand}>
                    <div className={styles.wordmark}>
                        {brandName}
                        <span className={styles.suffix}>{brandSuffix}</span>
                    </div>
                    <div className={styles.navRow}>
                        {buildNavLinks(destination?.name || 'Destination').map((item) => (
                            <Link key={item.label} to={item.url} className={styles.navLink}>
                                {item.label}
                            </Link>
                        ))}
                    </div>
                    <div className={styles.legalLines}>
                        <p>
                            © {year} All rights reserved {brandDomain} · Johann &amp; Samantha du Toit · Nairobi, Kenya · 0% commission
                        </p>
                        <p>
                            Powered by the Wilderness Bookings Platform · {contactPhone} · {contactEmail}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FooterDestinations;
